// DEV-ONLY placeholder for the widget-bundle backend.
//
// Until SQL/mytools_widget_support_migration.sql is applied, the API can't store or
// serve widget bundles. With REACT_APP_WIDGETS_MOCK=true (gitignored .env.local) the
// upload is processed in the browser — zip read, manifest validated, flattened into
// one document, CSP + bridge SDK injected — and kept in localStorage. Everything else
// (creating the type-3 tool, installing it, lists) still goes to the real API.
//
// The server's WidgetBundleService is the source of truth; this mirrors it closely
// enough to exercise the UI end-to-end. Never active in production builds.

import type { WidgetManifest } from './myToolsApi';

const STORAGE_KEY = 'widgets_mock_bundles';
const NETWORK_PREFIX = 'network:';
const SANDBOX_PERMISSIONS: Record<string, string> = { forms: 'allow-forms', popups: 'allow-popups', modals: 'allow-modals' };
const BRIDGE_PERMISSIONS = ['open-links', 'clipboard-write', 'ui:navigate', 'ui:notify', 'chat:prefill'];
const API_PREFIX = 'api:';
const API_SCOPES = ['insureds.read', 'policies.read', 'opportunities.read', 'tasks.read', 'notes.read', 'notes.write', 'tasks.write'];
const KNOWN_SURFACES = ['page', 'sidebar', 'tile'];
const ALLOWED_EXTENSIONS = ['.html', '.htm', '.js', '.mjs', '.css', '.json', '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.ico', '.woff', '.woff2', '.ttf', '.txt', '.md'];
const MIME: Record<string, string> = {
  '.png': 'image/png', '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.gif': 'image/gif', '.svg': 'image/svg+xml',
  '.webp': 'image/webp', '.ico': 'image/x-icon', '.woff': 'font/woff', '.woff2': 'font/woff2', '.ttf': 'font/ttf',
};

export interface MockWidgetBundle {
  toolId: string;
  manifest: WidgetManifest;
  html: string;
  version: string;
  revision: number;
  uploadedAt: string;
  warnings: string[];
}

export function isWidgetsMockEnabled(): boolean {
  return process.env.NODE_ENV !== 'production' && process.env.REACT_APP_WIDGETS_MOCK === 'true';
}

function readStore(): Record<string, MockWidgetBundle> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, MockWidgetBundle>) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, MockWidgetBundle>): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(store)); } catch { /* quota exceeded — leave as is */ }
}

/** Returns null unless mock mode is on, so callers can use it as a plain fallback. */
export function getMockWidgetBundle(toolId: string): MockWidgetBundle | null {
  if (!isWidgetsMockEnabled()) return null;
  return readStore()[String(toolId)] ?? null;
}

export function deleteMockWidgetBundle(toolId: string): void {
  const store = readStore();
  delete store[String(toolId)];
  writeStore(store);
}

// Install-time config values. The pre-migration API doesn't persist these for widgets,
// so the mock keeps them alongside the bundle to complete the momentum:init contract.
const CONFIG_KEY = 'widgets_mock_install_config';

export function getMockInstallConfig(toolId: string): Record<string, string> | null {
  if (!isWidgetsMockEnabled()) return null;
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, Record<string, string>>) : {};
    return all[String(toolId)] ?? null;
  } catch {
    return null;
  }
}

export function setMockInstallConfig(toolId: string, values: Record<string, string> | null | undefined): void {
  if (!isWidgetsMockEnabled() || !values) return;
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, Record<string, string>>) : {};
    all[String(toolId)] = { ...(all[String(toolId)] ?? {}), ...values };
    localStorage.setItem(CONFIG_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

/** Replace (not merge) — used when a key must be removed, e.g. un-pausing or re-granting a permission. */
export function replaceMockInstallConfig(toolId: string, values: Record<string, string>): void {
  if (!isWidgetsMockEnabled()) return;
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, Record<string, string>>) : {};
    all[String(toolId)] = { ...values };
    localStorage.setItem(CONFIG_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

export async function buildAndStoreMockBundle(toolId: string, file: File, fallbackName: string): Promise<MockWidgetBundle> {
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  let files: Map<string, Uint8Array>;
  let manifest: WidgetManifest;

  if (ext === '.zip') {
    files = await readZip(await file.arrayBuffer());
    const manifestKey = Array.from(files.keys()).find(k => k.toLowerCase() === 'manifest.json');
    if (!manifestKey) throw new Error('manifest.json is required at the root of the zip');
    try {
      manifest = JSON.parse(new TextDecoder().decode(files.get(manifestKey)!)) as WidgetManifest;
    } catch (e) {
      throw new Error(`manifest.json is not valid JSON: ${(e as Error).message}`);
    }
    if (!manifest.name) manifest.name = fallbackName;
  } else if (ext === '.html' || ext === '.htm') {
    files = new Map([[file.name, new Uint8Array(await file.arrayBuffer())]]);
    manifest = { name: fallbackName, version: '1.0.0', entry: file.name, permissions: [], surfaces: ['page'] };
  } else {
    throw new Error('Only .zip bundles or single .html files are supported');
  }

  const errors = normalizeAndValidate(manifest);
  if (errors.length) throw new Error(errors.length === 1 ? errors[0] : `Widget bundle is invalid:\n• ${errors.join('\n• ')}`);

  const entryKey = resolveKey(files, manifest.entry);
  if (!entryKey) throw new Error(`manifest.entry '${manifest.entry}' was not found in the bundle`);

  const warnings: string[] = [];
  const html = flatten(files, entryKey, manifest, warnings);
  // README / screenshots: pulled out of the bundle and kept as text / data URIs; the manifest is
  // rewritten to the served names the real API would use (assets/readme.md, assets/shot-N.ext).
  const assets = extractListingAssets(files, manifest);

  const store = readStore();
  const previous = store[String(toolId)];
  const bundle: MockWidgetBundle = {
    toolId: String(toolId),
    manifest,
    html,
    version: manifest.version,
    revision: (previous?.revision ?? 0) + 1,
    uploadedAt: new Date().toISOString(),
    warnings,
  };
  store[String(toolId)] = bundle;
  writeStore(store);
  writeMockAssets(String(toolId), assets);
  appendMockHistory(bundle, files.size, file.size, html.length);
  return bundle;
}

// ── listing assets (README, screenshots) ─────────────────────────────────────
const ASSETS_KEY = 'widgets_mock_assets';
const MAX_README_BYTES = 32 * 1024;
const MAX_SCREENSHOT_BYTES = 400 * 1024;

function extractListingAssets(files: Map<string, Uint8Array>, manifest: WidgetManifest): Record<string, string> {
  const out: Record<string, string> = {};
  if (manifest.readme) {
    const key = resolveKey(files, manifest.readme);
    if (!key) throw new Error(`manifest.readme '${manifest.readme}' was not found in the bundle`);
    const bytes = files.get(key)!;
    if (bytes.length > MAX_README_BYTES) throw new Error(`manifest.readme is larger than ${MAX_README_BYTES / 1024} KB`);
    out['assets/readme.md'] = new TextDecoder().decode(bytes);
    manifest.readme = 'assets/readme.md';
  }
  if (manifest.screenshots && manifest.screenshots.length) {
    const served: string[] = [];
    manifest.screenshots.forEach((shot, i) => {
      const key = resolveKey(files, shot);
      if (!key) throw new Error(`manifest.screenshots entry '${shot}' was not found in the bundle`);
      const bytes = files.get(key)!;
      if (bytes.length > MAX_SCREENSHOT_BYTES) throw new Error(`screenshot '${shot}' is larger than ${MAX_SCREENSHOT_BYTES / 1024} KB`);
      const ext = '.' + (key.split('.').pop() || '').toLowerCase();
      const name = `assets/shot-${i + 1}${ext}`;
      let binary = '';
      for (let j = 0; j < bytes.length; j += 0x8000) {
        binary += String.fromCharCode.apply(null, Array.prototype.slice.call(bytes.subarray(j, j + 0x8000)) as number[]);
      }
      out[name] = `data:${MIME[ext] ?? 'application/octet-stream'};base64,${btoa(binary)}`;
      served.push(name);
    });
    manifest.screenshots = served;
  }
  return out;
}

function writeMockAssets(toolId: string, assets: Record<string, string>): void {
  try {
    const raw = localStorage.getItem(ASSETS_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, Record<string, string>>) : {};
    if (Object.keys(assets).length) all[toolId] = assets; else delete all[toolId];
    localStorage.setItem(ASSETS_KEY, JSON.stringify(all));
  } catch { /* quota — listing assets are optional */ }
}

/** README text (for assets/readme.md) or a data: URI (screenshots); null when not mocked. */
export function getMockWidgetAsset(toolId: string, path: string): string | null {
  if (!isWidgetsMockEnabled()) return null;
  try {
    const raw = localStorage.getItem(ASSETS_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, Record<string, string>>) : {};
    return all[String(toolId)]?.[path] ?? null;
  } catch {
    return null;
  }
}

// Revision history, mirroring my_tool_widget_bundles so the "Versions" views work pre-migration.
const HISTORY_KEY = 'widgets_mock_bundle_history';

export interface MockWidgetBundleHistoryRow {
  id: number;
  toolId: string;
  revision: number;
  version: string;
  manifest: string;
  entryFile: string;
  fileCount: number;
  sizeBytes: number;
  flattenedSizeBytes: number;
  createdAt: string;
}

function appendMockHistory(bundle: MockWidgetBundle, fileCount: number, sizeBytes: number, flattenedSizeBytes: number): void {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, MockWidgetBundleHistoryRow[]>) : {};
    const rows = all[bundle.toolId] ?? [];
    rows.push({
      id: Date.now(),
      toolId: bundle.toolId,
      revision: bundle.revision,
      version: bundle.version,
      manifest: JSON.stringify(bundle.manifest),
      entryFile: bundle.manifest.entry,
      fileCount,
      sizeBytes,
      flattenedSizeBytes,
      createdAt: bundle.uploadedAt,
    });
    all[bundle.toolId] = rows.slice(-50);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

export function getMockWidgetBundleHistory(toolId: string): MockWidgetBundleHistoryRow[] | null {
  if (!isWidgetsMockEnabled()) return null;
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, MockWidgetBundleHistoryRow[]>) : {};
    const rows = all[String(toolId)];
    if (rows && rows.length) return rows;
    // Bundles stored before history existed: synthesise the current revision.
    const current = readStore()[String(toolId)];
    return current ? [{
      id: 0, toolId: current.toolId, revision: current.revision, version: current.version, manifest: JSON.stringify(current.manifest),
      entryFile: current.manifest.entry, fileCount: 0, sizeBytes: 0, flattenedSizeBytes: current.html.length, createdAt: current.uploadedAt,
    }] : [];
  } catch {
    return [];
  }
}

// ── manifest ──────────────────────────────────────────────────────────────────

function normalizeAndValidate(m: WidgetManifest): string[] {
  const errors: string[] = [];
  m.name = (m.name ?? '').trim();
  if (!m.name) errors.push('manifest.name is required');
  else if (m.name.length > 80) errors.push('manifest.name must be at most 80 characters');

  m.version = (m.version ?? '').trim();
  if (!m.version) errors.push('manifest.version is required');
  else if (!/^\d+\.\d+\.\d+([-+][0-9A-Za-z.-]+)?$/.test(m.version)) errors.push('manifest.version must be a semver string like 1.0.0');

  m.entry = (m.entry || 'index.html').trim().replace(/\\/g, '/').replace(/^\/+/, '');
  if (m.entry.includes('..') || m.entry.includes(':')) errors.push('manifest.entry must be a relative path inside the bundle');

  m.permissions = Array.from(new Set((m.permissions ?? []).map(p => String(p).trim()).filter(Boolean))) as WidgetManifest['permissions'];
  for (const p of m.permissions ?? []) {
    if (SANDBOX_PERMISSIONS[p] || BRIDGE_PERMISSIONS.includes(p)) continue;
    if (p.toLowerCase().startsWith(API_PREFIX)) {
      if (!API_SCOPES.includes(p.slice(API_PREFIX.length).toLowerCase())) errors.push(`unknown API scope '${p}' (known: ${API_SCOPES.map(s => API_PREFIX + s).join(', ')})`);
      continue;
    }
    if (p.toLowerCase().startsWith(NETWORK_PREFIX)) {
      if (!isValidNetworkOrigin(p.slice(NETWORK_PREFIX.length))) errors.push(`permission '${p}' must be a bare https:// origin (no path, query or wildcard)`);
      continue;
    }
    errors.push(`unknown permission '${p}'`);
  }

  m.surfaces = Array.from(new Set((m.surfaces ?? []).map(s => String(s).trim().toLowerCase()).filter(Boolean))) as WidgetManifest['surfaces'];
  if (!m.surfaces!.length) m.surfaces = ['page'];
  for (const s of m.surfaces!) if (!KNOWN_SURFACES.includes(s)) errors.push(`unknown surface '${s}'`);

  if (m.minHeight != null && (m.minHeight < 0 || m.minHeight > 4000)) errors.push('manifest.minHeight must be between 0 and 4000');

  m.changelog = (m.changelog ?? '').trim() || undefined;
  if (m.changelog && m.changelog.length > 2000) errors.push('manifest.changelog must be at most 2000 characters');

  const normalizePath = (p: string) => String(p).trim().replace(/\\/g, '/').replace(/^\/+/, '');
  const isRelative = (p: string) => !!p && !p.includes('..') && !p.includes(':') && p.length <= 200;
  m.readme = m.readme ? normalizePath(m.readme) : undefined;
  if (m.readme) {
    if (!isRelative(m.readme)) errors.push('manifest.readme must be a relative path inside the bundle');
    else if (!/\.(md|txt)$/i.test(m.readme)) errors.push('manifest.readme must point to a .md or .txt file');
  }
  m.screenshots = Array.from(new Set((m.screenshots ?? []).map(normalizePath).filter(Boolean)));
  if (m.screenshots.length > 4) errors.push('manifest.screenshots may list at most 4 images');
  for (const shot of m.screenshots) {
    if (!isRelative(shot)) errors.push(`manifest.screenshots entry '${shot}' must be a relative path inside the bundle`);
    else if (!/\.(png|jpe?g|webp|gif)$/i.test(shot)) errors.push(`manifest.screenshots entry '${shot}' must be a png, jpg, webp or gif`);
  }
  if (m.screenshots.length === 0) m.screenshots = undefined;

  m.config = m.config ?? [];
  const seen = new Set<string>();
  for (const f of m.config) {
    if (!f.key) errors.push('manifest.config entries need a key');
    else if (seen.has(f.key.toLowerCase())) errors.push(`manifest.config key '${f.key}' is duplicated`);
    else seen.add(f.key.toLowerCase());
    if (!f.label) f.label = f.key;
    if (!f.type) f.type = 'text';
  }
  return errors;
}

function isValidNetworkOrigin(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'https:' && u.pathname === '/' && !u.search && !u.hash && !u.hostname.includes('*')
      && u.origin.toLowerCase() === value.replace(/\/$/, '').toLowerCase();
  } catch {
    return false;
  }
}

function networkOrigins(m: WidgetManifest): string[] {
  return (m.permissions ?? []).filter(p => p.toLowerCase().startsWith(NETWORK_PREFIX)).map(p => p.slice(NETWORK_PREFIX.length).replace(/\/$/, ''));
}

function buildCsp(m: WidgetManifest): string {
  const origins = networkOrigins(m);
  const network = origins.length ? origins.join(' ') : "'none'";
  const img = origins.length ? ' ' + origins.join(' ') : '';
  return `default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:${img}; font-src data:; connect-src ${network}; form-action ${network}; frame-src 'none'; object-src 'none'; base-uri 'none'`;
}

// ── zip (stored + deflate entries) ────────────────────────────────────────────

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const DS = (window as unknown as { DecompressionStream?: new (format: string) => GenericTransformStream }).DecompressionStream;
  if (!DS) throw new Error('This browser cannot unzip bundles in mock mode (no DecompressionStream). Use a single .html file or a newer browser.');
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DS('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readZip(buf: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const bytes = new Uint8Array(buf);
  const dv = new DataView(buf);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('The upload is not a valid zip file');

  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const files = new Map<string, Uint8Array>();
  const decoder = new TextDecoder();

  for (let n = 0; n < count; n++) {
    if (dv.getUint32(p, true) !== 0x02014b50) throw new Error('Corrupt zip central directory');
    const method = dv.getUint16(p + 10, true);
    const compSize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOff = dv.getUint32(p + 42, true);
    const name = decoder.decode(bytes.subarray(p + 46, p + 46 + nameLen)).replace(/\\/g, '/');
    p += 46 + nameLen + extraLen + commentLen;

    if (!name || name.endsWith('/') || name.startsWith('__MACOSX/') || name.split('/').pop() === '.DS_Store') continue;
    if (name.startsWith('/') || name.includes(':') || name.split('/').some(seg => seg === '..' || seg === '.')) throw new Error(`Unsafe path in zip: '${name}'`);
    const ext = '.' + (name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) throw new Error(`File type not allowed in a widget bundle: '${name}'`);
    if (files.size >= 150) throw new Error('A widget bundle may contain at most 150 files');

    if (dv.getUint32(localOff, true) !== 0x04034b50) throw new Error('Corrupt zip entry');
    const dataStart = localOff + 30 + dv.getUint16(localOff + 26, true) + dv.getUint16(localOff + 28, true);
    const raw = bytes.slice(dataStart, dataStart + compSize);
    if (method === 0) files.set(name, raw);
    else if (method === 8) files.set(name, await inflateRaw(raw));
    else throw new Error(`Unsupported zip compression (${method}) in '${name}'`);
  }
  if (files.size === 0) throw new Error('The zip contains no usable files');
  return stripCommonRoot(files);
}

function stripCommonRoot(files: Map<string, Uint8Array>): Map<string, Uint8Array> {
  const keys = Array.from(files.keys());
  if (keys.some(k => k.toLowerCase() === 'manifest.json')) return files;
  const roots = Array.from(new Set(keys.map(k => k.split('/')[0])));
  if (roots.length !== 1 || keys.some(k => !k.includes('/'))) return files;
  const prefix = roots[0] + '/';
  return new Map(keys.map(k => [k.slice(prefix.length), files.get(k)!]));
}

// ── flatten ───────────────────────────────────────────────────────────────────

const RELATIVE_IMPORT = /(^|[\r\n;])\s*(import|export)\b[^;\r\n]*?\bfrom\s*['"](\.|\/)|\bimport\s*\(\s*['"](\.|\/)/i;

function flatten(files: Map<string, Uint8Array>, entryKey: string, manifest: WidgetManifest, warnings: string[]): string {
  const decoder = new TextDecoder();
  let html = decoder.decode(files.get(entryKey)!);
  const baseDir = entryKey.includes('/') ? entryKey.slice(0, entryKey.lastIndexOf('/') + 1) : '';
  const referenced = new Set<string>([entryKey]);

  html = html.replace(/<base\b[^>]*\/?>/gi, '');
  // Same as the server: a frame may navigate itself and CSP can't stop it, so the declarative
  // form is stripped here and the host frame kills the scripted form (second load event).
  const metaRefresh = /<meta\b[^>]*http-equiv\s*=\s*["']?refresh["']?[^>]*\/?>/gi;
  if (metaRefresh.test(html)) {
    html = html.replace(metaRefresh, '');
    warnings.push('<meta http-equiv="refresh"> was removed — a widget may not navigate its frame to another page');
  }

  html = html.replace(/<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi, (_m, attrs: string, body: string) => {
    const src = getAttr(attrs, 'src');
    if (src != null) {
      if (isExternal(src)) throw new Error(`External scripts are not allowed ('${src}'). Bundle all code inside the widget.`);
      const key = resolveRef(files, baseDir, src);
      if (!key) throw new Error(`Script '${src}' referenced by ${entryKey} was not found in the bundle`);
      referenced.add(key);
      body = decoder.decode(files.get(key)!);
      attrs = attrs.replace(/\s*\bsrc\s*=\s*("[^"]*"|'[^']*'|[^\s"'>]+)/i, '');
    }
    if (RELATIVE_IMPORT.test(body)) throw new Error('Cross-file ES module imports are not supported yet — bundle your JavaScript into a single file.');
    return `<script${attrs}>${body.replace(/<\/script/gi, '<\\/script')}</script>`;
  });

  html = html.replace(/<link\b([^>]*?)\/?>/gi, (m, attrs: string) => {
    const rel = getAttr(attrs, 'rel') ?? '';
    const href = getAttr(attrs, 'href');
    if (!/\bstylesheet\b/i.test(rel)) {
      if (href) warnings.push(`<link rel="${rel}" href="${href}"> was removed (only stylesheets are inlined)`);
      return '';
    }
    if (!href) return '';
    if (isExternal(href)) throw new Error(`External stylesheets are not allowed ('${href}'). Bundle your CSS inside the widget.`);
    const key = resolveRef(files, baseDir, href);
    if (!key) throw new Error(`Stylesheet '${href}' referenced by ${entryKey} was not found in the bundle`);
    referenced.add(key);
    const cssDir = key.includes('/') ? key.slice(0, key.lastIndexOf('/') + 1) : '';
    const css = inlineCssUrls(decoder.decode(files.get(key)!), cssDir, files, referenced, warnings);
    const media = getAttr(attrs, 'media');
    return `<style${media ? ` media="${media}"` : ''}>${css}</style>`;
  });

  html = html.replace(/<style\b([^>]*)>([\s\S]*?)<\/style\s*>/gi, (_m, attrs: string, css: string) =>
    `<style${attrs}>${inlineCssUrls(css, baseDir, files, referenced, warnings)}</style>`);

  html = html.replace(/<img\b([^>]*?)\/?>/gi, (m, attrs: string) => {
    const src = getAttr(attrs, 'src');
    if (!src || /^(data|blob):/i.test(src)) return m;
    if (isExternal(src)) {
      if (!networkOrigins(manifest).length) warnings.push(`External image '${src}' will be blocked by the widget's CSP (declare a network: permission or bundle it)`);
      return m;
    }
    const key = resolveRef(files, baseDir, src);
    if (!key) { warnings.push(`Image '${src}' referenced by ${entryKey} was not found in the bundle`); return m; }
    referenced.add(key);
    return `<img${attrs.replace(/\bsrc\s*=\s*("[^"]*"|'[^']*'|[^\s"'>]+)/i, `src="${toDataUri(key, files.get(key)!)}"`)}>`;
  });

  files.forEach((_bytes, key) => {
    if (!referenced.has(key) && key.toLowerCase() !== 'manifest.json') warnings.push(`'${key}' is not referenced by ${entryKey} and was ignored`);
  });

  const injection = `<meta http-equiv="Content-Security-Policy" content="${buildCsp(manifest)}">\n<script>${buildSdk(manifest)}</script>\n`;
  // Same rule as the server: prepend our prelude at offset 0 instead of anchoring on the author's
  // <head> (which could sit inside a comment or a string and leave the CSP where it is ignored).
  return `<!DOCTYPE html>\n<html>\n<head>\n${injection}</head>\n${html}`;
}

function inlineCssUrls(css: string, cssDir: string, files: Map<string, Uint8Array>, referenced: Set<string>, warnings: string[]): string {
  return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (m, _q: string, target: string) => {
    const t = target.trim();
    if (/^(data|blob):/i.test(t) || t.startsWith('#') || isExternal(t)) return m;
    const key = resolveRef(files, cssDir, t);
    if (!key) { warnings.push(`CSS url('${t}') was not found in the bundle`); return m; }
    referenced.add(key);
    return `url("${toDataUri(key, files.get(key)!)}")`;
  });
}

function toDataUri(key: string, bytes: Uint8Array): string {
  if (bytes.length > 500 * 1024) throw new Error(`'${key}' is larger than 500 KB; keep bundled assets small or reference them by https URL`);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.prototype.slice.call(bytes.subarray(i, i + 0x8000)) as number[]);
  }
  const ext = '.' + (key.split('.').pop() || '').toLowerCase();
  return `data:${MIME[ext] ?? 'application/octet-stream'};base64,${btoa(binary)}`;
}

// Same contract as the server-injected SDK (see WidgetBundleService.BuildSdk).
function buildSdk(manifest: WidgetManifest): string {
  // Measure the body's box, not documentElement.scrollHeight — the latter is never smaller
  // than the iframe viewport, so the frame could grow but never shrink.
  const autoResize = manifest.autoResize !== false
    ? "function measure(){var b=document.body;if(!b){return Math.ceil(document.documentElement.scrollHeight);}var cs=getComputedStyle(b);var px=function(v){return parseFloat(v)||0;};var bottom=b.getBoundingClientRect().top+px(cs.borderTopWidth)+px(cs.paddingTop);var rg=document.createRange();rg.selectNodeContents(b);var rr=rg.getBoundingClientRect();if(rr.height>0&&rr.bottom>bottom){bottom=rr.bottom;}var c=b.children;for(var i=0;i<c.length;i++){var s=getComputedStyle(c[i]);if(s.position==='fixed'||s.display==='none'){continue;}var cr=c[i].getBoundingClientRect();var cb=cr.bottom+px(s.marginBottom);if(cb>bottom){bottom=cb;}}return Math.ceil(bottom+px(cs.paddingBottom)+px(cs.borderBottomWidth)+px(cs.marginBottom));}"
      + "function report(){var h=measure();if(h&&h!==reportedHeight){reportedHeight=h;post('momentum:resize',{height:h});}}var reportedHeight=0;"
      + "if(window.ResizeObserver){var ro=new ResizeObserver(report);ro.observe(document.documentElement);if(document.body){ro.observe(document.body);}else{document.addEventListener('DOMContentLoaded',function(){ro.observe(document.body);});}}"
      + "window.addEventListener('load',report);setTimeout(report,50);"
    : '';
  return `(function(){
if(window.Momentum){return;}
var listeners={},ctx=null,ready=false,pending={},seq=0,themeApplied=false;
function post(type,payload){try{window.parent.postMessage(Object.assign({type:type,protocol:1},payload||{}),'*');}catch(e){}}
function request(type,payload,timeout){return new Promise(function(res,rej){var id='r'+(++seq);var t=setTimeout(function(){delete pending[id];rej(new Error('Request timed out'));},timeout||20000);pending[id]={res:res,rej:rej,t:t};post(type,Object.assign({id:id},payload||{}));});}
function applyTheme(){if(!ctx){return;}themeApplied=true;var r=document.documentElement;r.setAttribute('data-theme',ctx.theme||'dark');var t=ctx.tokens||{};for(var k in t){r.style.setProperty('--momentum-'+k.replace(/[A-Z]/g,function(c){return '-'+c.toLowerCase();}),t[k]);}}
window.addEventListener('message',function(ev){
if(ev.source!==window.parent){return;}
var d=ev.data;if(!d||typeof d.type!=='string'||d.type.indexOf('momentum:')!==0){return;}
if(d.type==='momentum:fetch:result'||d.type==='momentum:connect:result'||d.type==='momentum:storage:result'){var p=pending[d.id];if(p){clearTimeout(p.t);delete pending[d.id];if(d.ok){p.res(d.type==='momentum:storage:result'?d.value:d.data);}else{var err=new Error(d.error||'Request failed');err.status=d.status;p.rej(err);}}return;}
if(d.type==='momentum:init'){ctx=d.context||{};}
if(d.type==='momentum:context'&&ctx){for(var k in (d.context||{})){ctx[k]=d.context[k];}}
if(d.type==='momentum:theme'&&ctx){ctx.theme=d.theme;if(d.tokens){ctx.tokens=d.tokens;}}
if(d.type==='momentum:config'&&ctx){ctx.config=d.config;}
if(themeApplied&&(d.type==='momentum:init'||d.type==='momentum:theme')){applyTheme();}
var ls=listeners[d.type]||[];for(var i=0;i<ls.length;i++){try{ls[i](d.context!==undefined?d.context:d);}catch(e){}}
});
var errorsSent=0;
function reportError(message,source,line){if(errorsSent>=25){return;}errorsSent++;post('momentum:error',{message:String(message||'Script error').slice(0,500),source:String(source||'').slice(0,200),line:Number(line)||0});}
window.addEventListener('error',function(e){reportError(e&&e.message,e&&e.filename,e&&e.lineno);});
window.addEventListener('unhandledrejection',function(e){var r=e&&e.reason;reportError(r&&r.message?r.message:r,'',0);});
window.Momentum={
on:function(type,fn){var k='momentum:'+type;(listeners[k]=listeners[k]||[]).push(fn);if(type==='init'&&ctx){fn(ctx);}},
context:function(){return ctx;},
tokens:function(){return ctx&&ctx.tokens?ctx.tokens:{};},
applyTheme:applyTheme,
resize:function(h){post('momentum:resize',{height:h});},
open:function(url){post('momentum:open',{url:url});},
copy:function(text){post('momentum:clipboard',{text:text});},
navigate:function(path){post('momentum:navigate',{path:path});},
notify:function(message,opts){opts=opts||{};post('momentum:notify',{message:message,level:opts.level||'info'});},
chat:{prefill:function(text){post('momentum:chat:prefill',{text:text});}},
fetch:function(scope,path,opts){opts=opts||{};return request('momentum:fetch',{scope:scope,path:path,method:opts.method||'GET',query:opts.query||null,body:opts.body===undefined?null:opts.body},opts.timeout);},
connect:function(connection,path,opts){opts=opts||{};return request('momentum:connect',{connection:connection,path:path||'',method:opts.method||'GET',query:opts.query||null,body:opts.body===undefined?null:opts.body},opts.timeout);},
storage:{
get:function(key){return request('momentum:storage',{op:'get',key:key});},
set:function(key,value){return request('momentum:storage',{op:'set',key:key,value:value});},
remove:function(key){return request('momentum:storage',{op:'remove',key:key});},
keys:function(){return request('momentum:storage',{op:'keys'});},
getAll:function(){return request('momentum:storage',{op:'getAll'});},
clear:function(){return request('momentum:storage',{op:'clear'});}
},
ready:function(){if(!ready){ready=true;post('momentum:ready');}}
};${autoResize}
if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',window.Momentum.ready);}else{window.Momentum.ready();}
})();`;
}

// ── helpers ───────────────────────────────────────────────────────────────────

function isExternal(url: string): boolean {
  return /^(https?:)?\/\//i.test(url.trim());
}

function getAttr(attrs: string, name: string): string | null {
  const m = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i').exec(attrs);
  return m ? (m[1] ?? m[2] ?? m[3] ?? '') : null;
}

function resolveRef(files: Map<string, Uint8Array>, baseDir: string, reference: string): string | null {
  let path = reference.trim();
  const cut = path.search(/[?#]/);
  if (cut >= 0) path = path.slice(0, cut);
  if (!path) return null;
  const combined = path.startsWith('/') ? path.replace(/^\/+/, '') : baseDir + path;
  const segments: string[] = [];
  for (const seg of combined.split('/')) {
    if (!seg || seg === '.') continue;
    if (seg === '..') { if (!segments.length) return null; segments.pop(); continue; }
    segments.push(seg);
  }
  return resolveKey(files, segments.join('/'));
}

function resolveKey(files: Map<string, Uint8Array>, path: string): string | null {
  if (files.has(path)) return path;
  const lower = path.toLowerCase();
  return Array.from(files.keys()).find(k => k.toLowerCase() === lower) ?? null;
}
